import { useMemo, useCallback } from 'react'
import { PlatformUserDomainRecord } from '../../../../models/domain/platform-user-domain-record'
import { UserPreferencesApi } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { addToRecentsList, filterValidIds } from '../utils/recents-logic'

/** One shared fallback so an unset preference reads as the same array on every render. */
const NO_IDS: number[] = []

/**
 * useRecents — reads and writes the recently selected domains list
 * via the preferences API at the unscoped layer.
 *
 * @param userPreferences - UserPreferencesApi instance (unscoped)
 * @param domainMap - Map of domainId → PlatformUserDomainRecord for validation
 * @param maxRecents - Maximum number of recents to keep
 * @returns recents array and addToRecents function
 */
export function useRecents(
    userPreferences: UserPreferencesApi,
    domainMap: Map<number, PlatformUserDomainRecord>,
    maxRecents: number,
): {
    recents: number[]
    addToRecents: (domainId: number) => void
} {
    const rawRecents: number[] = userPreferences.get(['domain_selector_recents'], NO_IDS)

    const recents = useMemo(() => {
        if (userPreferences.isLoading) {
            return NO_IDS
        }

        return filterValidIds(rawRecents, domainMap)
    }, [userPreferences.isLoading, rawRecents, domainMap])

    const addToRecents = useCallback(
        (domainId: number): void => {
            const updated = addToRecentsList(rawRecents, domainId, maxRecents)
            userPreferences.set(['domain_selector_recents'], updated)
        },
        [rawRecents, maxRecents, userPreferences],
    )

    return {
        recents,
        addToRecents,
    }
}
